import { graphqlHttpUrl } from "./config";
import {
  createOAuthCodeChallengeS256,
  createOAuthCodeVerifier,
} from "./oauthPkce";
import { saveOAuthPkce, setOAuthReturnPath } from "./oauthSession";

function randomState(): string {
  const a = new Uint8Array(24);
  crypto.getRandomValues(a);
  return Array.from(a, (x) => x.toString(16).padStart(2, "0")).join("");
}

export function isOAuthConfigured(): boolean {
  return Boolean(
    import.meta.env.VITE_TOKO_OAUTH_AUTHORIZE_URL?.trim() &&
      import.meta.env.VITE_TOKO_OAUTH_CLIENT_ID?.trim() &&
      import.meta.env.VITE_TOKO_OAUTH_REDIRECT_URI?.trim(),
  );
}

export function oauthTokenUrl(): string {
  const explicit = import.meta.env.VITE_TOKO_OAUTH_TOKEN_URL?.trim();
  if (explicit) return explicit.replace(/\/$/, "");
  if (import.meta.env.DEV && typeof window !== "undefined") {
    return `${window.location.origin}/oauth/token`;
  }
  const authorize = import.meta.env.VITE_TOKO_OAUTH_AUTHORIZE_URL?.trim();
  if (authorize) {
    return authorize.replace(/\/authorize\/?$/, "/token");
  }
  try {
    return `${new URL(graphqlHttpUrl()).origin}/oauth/token`;
  } catch {
    return "http://localhost:8008/oauth/token";
  }
}

export function isOAuthCallbackPath(): boolean {
  const raw = (typeof window !== "undefined" ? window.location.pathname : "/").replace(
    /\/+$/,
    "",
  );
  return raw === "/staff/oauth/callback";
}

export async function redirectToTokoOAuthLogin(opts?: {
  businessId?: string;
  branchId?: string;
  returnPath?: string;
}): Promise<void> {
  const authorizeBase = import.meta.env.VITE_TOKO_OAUTH_AUTHORIZE_URL?.trim();
  const clientId = import.meta.env.VITE_TOKO_OAUTH_CLIENT_ID?.trim();
  const redirectUri = import.meta.env.VITE_TOKO_OAUTH_REDIRECT_URI?.trim();
  if (!authorizeBase || !clientId || !redirectUri) {
    throw new Error("OAuth не настроен");
  }

  const bid = opts?.businessId?.trim() ?? "";
  const br = opts?.branchId?.trim() ?? "";
  if (bid && !/^[a-f0-9]{24}$/i.test(bid)) {
    throw new Error("Некорректный ID бизнеса");
  }
  if (br && !/^[a-f0-9]{24}$/i.test(br)) {
    throw new Error("Некорректный ID филиала");
  }

  const verifier = createOAuthCodeVerifier();
  const challenge = await createOAuthCodeChallengeS256(verifier);
  const state = randomState();
  saveOAuthPkce(verifier, state);
  setOAuthReturnPath(opts?.returnPath?.trim() || "/");

  const u = new URL(authorizeBase);
  u.searchParams.set("response_type", "code");
  u.searchParams.set("client_id", clientId);
  u.searchParams.set("redirect_uri", redirectUri);
  u.searchParams.set("state", state);
  u.searchParams.set("code_challenge", challenge);
  u.searchParams.set("code_challenge_method", "S256");
  if (bid) u.searchParams.set("business_id", bid);
  if (br) u.searchParams.set("branch_id", br);
  window.location.href = u.toString();
}
